/**
 * R31 (2026-06-05) — Auth callback per Emergent Google OAuth.
 *
 * Flow:
 * 1. L'utente atterra qui dopo aver autenticato con Google.
 * 2. URL: `<origin>/auth/google/callback#session_id=...`
 * 3. Estraiamo session_id, POST `/api/auth/google/exchange`.
 * 4. Backend chiama Emergent /session-data, estrae email, e:
 *    - se utente esistente → mint JWT cookies → ritorna kind=login
 *    - se nuovo → ritorna kind=signup_needed con signup_token
 * 5. Routing:
 *    - kind=login → `/oggi` (o destinazione salvata) + refresh AuthContext
 *    - kind=signup_needed → `/completa-registrazione` con signup_token in state
 *
 * Race-condition prevention (per playbook Emergent):
 * - useRef per il flag di processing (NON useState): set sincrono al primo render
 * - L'AuthProvider skippa /auth/me se trova session_id nell'hash (vedi AuthContext)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import api, { formatApiError, setAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // CRITICAL: lock sincrono al primo render (StrictMode-safe per useRef).
    // Questo è uno dei pochi casi accettabili di "fire-and-forget" perché
    // l'AuthCallback è una pagina-bridge che naviga via immediatamente.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        const session_id = m ? decodeURIComponent(m[1]) : "";
        if (!session_id) {
          throw new Error("Sessione Google mancante. Riprova dal login.");
        }
        // Rimuovi il fragment dall'URL per evitare retry su refresh
        try {
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, "", cleanUrl);
        } catch { /* ignore */ }

        const { data } = await api.post("/auth/google/exchange", { session_id });

        if (data.kind === "login") {
          // Utente esistente: cookie già impostati dal backend, refresh contesto
          if (data.user?.access_token) {
            setAuthToken(data.user.access_token);
          }
          await refresh();
          // Vai dritto al cruscotto operatore (consistente con /auth/login)
          nav("/oggi", { replace: true });
          return;
        }

        if (data.kind === "signup_needed") {
          // Nuovo utente: passa il signup_token in state (più sicuro di query string)
          nav("/completa-registrazione", {
            replace: true,
            state: {
              signup_token: data.signup_token,
              email: data.email,
              name: data.name,
              picture: data.picture,
            },
          });
          return;
        }

        throw new Error("Risposta di autenticazione non riconosciuta.");
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || e.message || "Errore di autenticazione Google.";
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [nav, refresh]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-rose-200 p-6 shadow-sm text-center" data-testid="auth-callback-error">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center mb-3">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Autenticazione fallita</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <Button onClick={() => (window.location.href = "/login")} className="w-full" data-testid="auth-callback-back-btn">
            Torna al login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600" data-testid="auth-callback-loading">
      <div className="text-center">
        <Loader2 className="w-7 h-7 animate-spin mx-auto mb-3 text-amber-600" />
        <p className="text-sm font-medium">Accesso in corso…</p>
      </div>
    </div>
  );
}
